import { AutocompleteCarrierInput, FreeFormCarrierInput } from "./CarrierInput";
import MemoizedTowerTableRow from "./MemoizedTowerTableRow";
import {
  Error as ErrorIcon,
  MessageOutlined as MessageOutlinedIcon,
} from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import * as numbers from "common/numbers";
import ConditionalTooltip from "components/common/ConditionalTooltip";
import FeatureFlagged from "components/common/FeatureFlagged";
import IconButton from "components/common/IconButton";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import TowerLayerIncludeSir from "components/layers/TowerLayerIncludeSir";
import { DYNAMIC_AUTO_COMPLETE } from "flags";
import NumberFormat from "react-number-format";
import { connect, useSelector } from "react-redux";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as claimsSelectors from "store/selectors/input/claims/claimsSelectors";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import * as towerPricingSelectors from "store/selectors/pricing/tower/towerPricingSelectors";
import * as stateSelectors from "store/selectors/stateSelectors";
import * as userSelectors from "store/selectors/user/userSelectors";

const findMatchingPriorLayer = (currentLayer, priorLayers) => {
  if (!currentLayer || !priorLayers) return null;

  return priorLayers.find(
    (pl) =>
      pl.attachment === currentLayer.attachment &&
      pl.limit === currentLayer.limit
  );
};

const dealQuality = (good) => {
  if (good == null) {
    return "";
  }
  if (good < 0) {
    return "table-cell-bad-deal";
  }
  if (good > 0) {
    return "table-cell-good-deal";
  }
  return "";
};

const shareCarrierVal = (share) => {
  const carrier = share?.carrier;
  if (carrier && carrier.trim()) return carrier;
  return "[Unknown]";
};

const updatedPricingValues = ({
  grossPremium = null,
  grossRPM = null,
  grossRPMRelativeToLayerBelow = null,
} = {}) => ({
  grossPremium,
  grossRPM,
  grossRPMRelativeToLayerBelow,
});

const NumberCell = ({ value, cellStyle, good, ...numberFormatProps }) => {
  if (value == null || isNaN(value)) {
    return <TableCell align={"right"} />;
  } else {
    return (
      <TableCell align={"right"} style={cellStyle}>
        <NumberFormat
          displayType={"text"}
          thousandSeparator={true}
          defaultValue={""}
          decimalScale={0}
          className={`monospace ${dealQuality(good)}`}
          {...numberFormatProps}
          value={value}
        />
      </TableCell>
    );
  }
};

const COLUMNS = [
  {
    key: "addLayer",
    title: "",
    width: 0,
    align: "right",
    description: "",
    visibilityTag: "<BrokerTower>.table.addLayer",
    Cell: ({ layerIndex, isNewRow, dispatch }) => (
      <TableCell align={"left"}>
        {isNewRow ? (
          <></>
        ) : (
          <IconButton
            icon={AddIcon}
            onClick={() => {
              dispatch?.setIsExpanded?.(false);
              dispatch?.addLayer?.(layerIndex);
            }}
          />
        )}
      </TableCell>
    ),
  },
  {
    key: "deleteLayer",
    title: "",
    width: 0,
    align: "right",
    description: "",
    visibilityTag: "<BrokerTower>.table.deleteLayer",
    Cell: ({ layerIndex, isNewRow, dispatch }) => (
      <TableCell align={"left"}>
        {isNewRow ? (
          <></>
        ) : (
          <IconButton
            icon={DeleteIcon}
            onClick={() => {
              dispatch?.setIsExpanded?.(false);
              dispatch?.deleteLayer?.(layerIndex);
            }}
          />
        )}
      </TableCell>
    ),
  },
  {
    key: "numLayer",
    title: "",
    width: 0,
    align: "left",
    description: "",
    visibilityTag: "<BrokerTower>.table.index",
    Cell: ({ layerIndex }) => <NumberCell value={layerIndex + 1} />,
  },
  {
    key: "carrier",
    title: "Carrier",
    width: 200,
    align: "left",
    description: "The name of the insurer.",
    visibilityTag: "<BrokerTower>.table.carrier",
    Cell: ({ layer, layerIndex, dispatch }) => {
      const carrierChanged = (value) => {
        const carrier = value ?? "";
        if (carrier.includes("/")) {
          dispatch?.setIsExpanded?.(true);
          carrier.split("/").forEach((c, i) => {
            dispatch?.updateShare?.({
              layerIndex,
              shareIndex: i,
              values: {
                carrier: c,
                share: null,
              },
            });
          });
        } else {
          dispatch?.updateShare?.({
            layerIndex,
            shareIndex: 0,
            values: {
              carrier: value,
              share: 100,
            },
          });
        }
      };
      return (
        <TableCell align={"left"}>
          <ConditionalTooltip
            conditional={(layer?.shares ?? []).length > 1}
            title={"Click to see the shares."}
          >
            {(layer?.shares ?? []).length > 1 ? (
              <TextField
                value={layer?.shares.map(shareCarrierVal).join(" / ")}
                fullWidth
                onClick={() => dispatch?.setIsExpanded?.(true)}
                variant={"filled"}
                autoComplete={"off"}
                size={"small"}
                hiddenLabel
                disabled
              />
            ) : (
              <FeatureFlagged
                on={AutocompleteCarrierInput}
                off={FreeFormCarrierInput}
                flag={DYNAMIC_AUTO_COMPLETE}
                value={layer?.shares?.[0]?.carrier ?? ""}
                onChangeValue={carrierChanged}
              />
            )}
          </ConditionalTooltip>
        </TableCell>
      );
    },
  },
  {
    key: "expandShare",
    title: "",
    width: 0,
    align: "left",
    description: "Expand the layer to see the shares.",
    visibilityTag: "<BrokerTower>.table.expandShare",
    Cell: ({ layer, isNewRow, isExpanded, dispatch }) => (
      <TableCell align={"left"}>
        {layer?.shares?.some((share) => share.comments?.length > 0) && (
          <MessageOutlinedIcon />
        )}
        {isNewRow ? (
          <></>
        ) : isExpanded ? (
          <IconButton
            icon={ExpandLessIcon}
            onClick={() => dispatch?.setIsExpanded?.(false)}
          />
        ) : (
          <IconButton
            icon={ExpandMoreIcon}
            onClick={() => dispatch?.setIsExpanded?.(true)}
          />
        )}
      </TableCell>
    ),
  },
  {
    key: "limit",
    title: "Limit",
    width: 150,
    align: "right",
    description: "The layer limit.",
    visibilityTag: "<BrokerTower>.table.limit",
    Cell: ({ layer, layerIndex, dispatch }) => (
      <TableCell align={"right"}>
        <PrettyNumberTextField
          inputProps={{ style: { textAlign: "right" } }}
          value={layer?.limit ?? ""}
          fullWidth
          onChangeNumber={(limit) => dispatch?.updateLimit?.(layerIndex, limit)}
          name={"limit"}
          variant={"filled"}
          size={"small"}
          hiddenLabel
        />
      </TableCell>
    ),
  },
  {
    key: "attachment",
    title: "Attachment",
    width: 150,
    align: "right",
    description: "The layer attachment.",
    visibilityTag: "<BrokerTable>.table.attachment",
    Cell: ({ layer, layerIndex, dispatch }) => (
      <TableCell align={"right"}>
        <PrettyNumberTextField
          inputProps={{ style: { textAlign: "right" } }}
          value={layer?.attachment ?? ""}
          fullWidth
          onChangeNumber={(attachment) =>
            dispatch?.updateAttachment?.(layerIndex, attachment)
          }
          name={"attachment"}
          variant={"filled"}
          size={"small"}
          hiddenLabel
        />
      </TableCell>
    ),
  },
  {
    key: "includeSir",
    title: "Include SIR",
    width: 180,
    align: "left",
    description:
      "Select a self-insured retention to include in the effective attachment.",
    visibilityTag: "<BrokerTower>.table.includeSir",
    defaultVisibility: false,
    Cell: ({ layer, layerIndex, isNewRow, dispatch }) => {
      const sirs =
        useSelector(claimsSelectors.selectSelfInsuredRetentions) || [];
      return (
        <TableCell align={"left"}>
          {!isNewRow && sirs?.length && (
            <TowerLayerIncludeSir layer={layer} layerIndex={layerIndex} />
          )}
        </TableCell>
      );
    },
  },
  {
    key: "effectiveAttachment",
    title: "Effective Attachment",
    width: 150,
    align: "right",
    description:
      "The effective attachment point including the self-insured retention.",
    visibilityTag: "<BrokerTower>.table.effectiveAttachment",
    defaultVisibility: false,
    Cell: ({ layer }) => <NumberCell value={layer?.effectiveAttachment} />,
  },
  {
    key: "grossPremium",
    title: "Gross Premium",
    width: 150,
    align: "right",
    description: "The 100% bound premium.",
    visibilityTag: "<BrokerTower>.table.grossPremium",
    Cell: ({ layer, layerIndex, dispatch }) => (
      <TableCell align={"right"} style={{ minWidth: "150px" }}>
        <PrettyNumberTextField
          inputProps={{ style: { textAlign: "right" } }}
          value={layer?.grossPremium ?? ""}
          fullWidth
          onChangeNumber={(grossPremium) =>
            dispatch?.updateLayer?.(
              layerIndex,
              updatedPricingValues({ grossPremium })
            )
          }
          offFormatter={numbers.dpString(0)}
          variant={"filled"}
          size={"small"}
          hiddenLabel
        />
      </TableCell>
    ),
  },
  {
    key: "premiumChange",
    title: "Δ Premium",
    width: 150,
    align: "right",
    description: "The year-over-year change in premium (current - prior).",
    visibilityTag: "<BrokerTower>.table.premiumChange",
    hideUnlessRenewal: true,
    Cell: ({ layer, priorLayers }) => {
      if (!layer?.grossPremium)
        return <TableCell align={"right"}>{"-"}</TableCell>;

      const currentPremium = parseFloat(layer.grossPremium);
      if (isNaN(currentPremium))
        return <TableCell align={"right"}>{"-"}</TableCell>;

      const priorLayer = findMatchingPriorLayer(layer, priorLayers);
      if (!priorLayer?.grossPremium)
        return <TableCell align={"right"}>{"-"}</TableCell>;

      const priorPremium = parseFloat(priorLayer.grossPremium);
      if (isNaN(priorPremium))
        return <TableCell align={"right"}>{"-"}</TableCell>;

      const change = currentPremium - priorPremium;
      const percentChange = ((change / priorPremium) * 100).toFixed(2);
      const tooltipText = `${percentChange}% change`;

      return (
        <Tooltip title={tooltipText}>
          <TableCell align={"right"}>
            <NumberFormat
              displayType={"text"}
              thousandSeparator={true}
              defaultValue={""}
              decimalScale={0}
              prefix={change > 0 ? "+" : ""}
              className={`monospace ${dealQuality(
                change > 0 ? 1 : change < 0 ? -1 : 0
              )}`}
              value={change}
            />
          </TableCell>
        </Tooltip>
      );
    },
  },
  {
    key: "premiumChangePercent",
    title: "Δ GWP %",
    width: 150,
    align: "right",
    description:
      "The year-over-year percentage change in premium ((current - prior) / prior * 100).",
    visibilityTag: "<BrokerTower>.table.premiumChangePercent",
    hideUnlessRenewal: true,
    Cell: ({ layer, priorLayers }) => {
      if (!layer?.grossPremium)
        return <TableCell align={"right"}>{"-"}</TableCell>;

      const currentPremium = parseFloat(layer.grossPremium);
      if (isNaN(currentPremium))
        return <TableCell align={"right"}>{"-"}</TableCell>;

      const priorLayer = findMatchingPriorLayer(layer, priorLayers);
      if (!priorLayer?.grossPremium)
        return <TableCell align={"right"}>{"-"}</TableCell>;

      const priorPremium = parseFloat(priorLayer.grossPremium);
      if (isNaN(priorPremium))
        return <TableCell align={"right"}>{"-"}</TableCell>;

      const change = currentPremium - priorPremium;
      const percentChange = (change / priorPremium) * 100;
      const tooltipText = `${
        change > 0 ? "+" : ""
      }${change.toLocaleString()} change`;

      return (
        <Tooltip title={tooltipText}>
          <TableCell align={"right"}>
            <NumberFormat
              displayType={"text"}
              thousandSeparator={true}
              defaultValue={""}
              decimalScale={2}
              suffix={"%"}
              prefix={percentChange > 0 ? "+" : ""}
              className={`monospace ${dealQuality(
                percentChange > 0 ? 1 : percentChange < 0 ? -1 : 0
              )}`}
              value={percentChange}
            />
          </TableCell>
        </Tooltip>
      );
    },
  },
  {
    key: "brokerage",
    title: "% Brokerage",
    width: 80,
    align: "right",
    description: "The effective brokerage for the layer.",
    visibilityTag: "<BrokerTable>.table.brokerage",
    hideGross: true,
    Cell: ({ layer }) => (
      <NumberCell value={layer?.brokerage} decimalScale={1} />
    ),
  },
  {
    key: "netPremium",
    title: "Net Premium",
    width: 100,
    align: "right",
    description: "The net 100% bound premium.",
    visibilityTag: "<BrokerTower>.table.netPremium",
    hideGross: true,
    Cell: ({ layer }) =>
      layer?.errors?.netPremium ? (
        <TableCell sx={{ width: 150 }} align={"right"}>
          <Tooltip title={layer.errors.netPremium}>
            <ErrorIcon
              color={"error"}
              sx={{ fontSize: "1rem", marginLeft: 0.5 }}
            />
          </Tooltip>
        </TableCell>
      ) : (
        <NumberCell value={layer?.netPremium} />
      ),
  },
  {
    key: "grossRPM",
    title: "Gross RPM",
    width: 100,
    align: "right",
    description: "100% bound premium / limit.",
    visibilityTag: "<BrokerTower>.table.grossRPM",
    hideNet: true,
    Cell: ({ layerIndex, layer, dispatch }) => (
      <TableCell align={"right"} style={{ minWidth: "150px" }}>
        <PrettyNumberTextField
          inputProps={{ style: { textAlign: "right" } }}
          value={layer?.grossRPM ?? ""}
          fullWidth
          onChangeNumber={(grossRPM) =>
            dispatch?.updateLayer?.(
              layerIndex,
              updatedPricingValues({ grossRPM })
            )
          }
          offFormatter={numbers.dpString(0)}
          variant={"filled"}
          size={"small"}
          hiddenLabel
        />
      </TableCell>
    ),
  },
  {
    key: "netRPM",
    title: "Net RPM",
    width: 80,
    align: "right",
    description: "100% bound premium / limit.",
    visibilityTag: "<BrokerTower>.table.netRPM",
    hideGross: true,
    Cell: ({ layer }) => <NumberCell value={layer?.netRPM} />,
  },
  {
    key: "grossRelativeRate",
    title: "Relativity",
    width: 80,
    align: "right",
    description: "The ratio of the RPM of this layer with the layer below.",
    visibilityTag: "<BrokerTower>.table.grossRelativeRate",
    hideNet: true,
    Cell: ({ layerIndex, layer, dispatch }) => (
      <TableCell align={"right"}>
        <PrettyNumberTextField
          inputProps={{ style: { textAlign: "right" } }}
          value={
            layer?.grossRPMRelativeToLayerBelow != null
              ? 100 * layer?.grossRPMRelativeToLayerBelow
              : ""
          }
          fullWidth
          onChangeNumber={(grossRPMRelativeToLayerBelow) =>
            dispatch?.updateLayer?.(
              layerIndex,
              updatedPricingValues({
                grossRPMRelativeToLayerBelow:
                  grossRPMRelativeToLayerBelow != null
                    ? 0.01 * grossRPMRelativeToLayerBelow
                    : null,
              })
            )
          }
          suffix={"%"}
          offFormatter={numbers.dpString(2)}
          variant={"filled"}
          size={"small"}
          hiddenLabel
          disabled={layerIndex === 0}
        />
      </TableCell>
    ),
  },
  {
    key: "netRelativeRate",
    title: "Net Relativity",
    width: 80,
    align: "right",
    description: "The ratio of the net RPM of this layer with the layer below.",
    visibilityTag: "<BrokerTower>.table.netRelativeRate",
    hideGross: true,
    Cell: ({ layer }) => (
      <NumberCell
        suffix={"%"}
        decimalScale={2}
        fixedDecimalScale
        value={(layer?.netRPMRelativeToLayerBelow ?? NaN) * 100}
      />
    ),
  },
];

export const TowerTable = ({
  isNet,
  layers,
  selectedIndex,
  baseLayerLosses,
  baseLayerAttachment,
  componentVisibility,
  priorLayers,
}) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const isRenewal = !!useSelector(programSelectors.getPriorSubmissionId);

  const visibleColumns = COLUMNS.filter(
    (c) =>
      (isNet ? !c.hideNet : !c.hideGross) &&
      (!c.hideUnlessRenewal || isRenewal) &&
      (c.visibilityTag === undefined ||
        (componentVisibility?.[c.visibilityTag] ?? c.defaultVisibility ?? true))
  );

  return (
    <TableContainer>
      <Table size={"small"} className={"condensed-table"}>
        <TableHead>
          <TableRow>
            {visibleColumns.map(
              (
                {
                  key,
                  title,
                  description,
                  makeDescription,
                  align,
                  width,
                  cellStyle,
                },
                i
              ) => {
                if (makeDescription) {
                  description = makeDescription({
                    baseLayerLosses,
                    baseLayerAttachment,
                  });
                }
                return (
                  <ConditionalTooltip
                    key={key}
                    conditional={description}
                    title={description}
                  >
                    <TableCell
                      align={align}
                      valign={"bottom"}
                      style={{
                        minWidth: width,
                        verticalAlign: "bottom",
                        ...cellStyle,
                      }}
                      key={i}
                    >
                      {translateLabel(`TOWER_TABLE.${key}`, { default: title })}
                    </TableCell>
                  </ConditionalTooltip>
                );
              }
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {[...(layers ?? []), {}].map((layer, layerIndex) => (
            <MemoizedTowerTableRow
              columns={visibleColumns}
              key={layerIndex}
              layer={layer}
              layerIndex={layerIndex}
              isSelectedRow={selectedIndex === layerIndex}
              isNewRow={layerIndex === layers?.length}
              priorLayers={priorLayers}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const mapStateToProps = (state) => {
  const priorState = stateSelectors.selectPriorState(state);
  return {
    layers: towerPricingSelectors.getTower(state),
    selectedIndex: state?.pricing?.tower?.selectedLayer ?? null,
    baseLayerLosses:
      pricingSelectors.getNumberOfLossesForPricingPretty(state) ?? "unknown",
    baseLayerAttachment: state?.pricing?.baseLayer?.attachment ?? null,
    componentVisibility:
      userSelectors.selectUserConfig(state).componentVisibility ?? {},
    priorLayers: towerPricingSelectors.getTower(priorState),
  };
};

export default connect(mapStateToProps)(TowerTable);
